import React, { useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Avatar, Box, Breadcrumbs, Divider, IconButton, ListItemIcon, Menu, MenuItem,
  Stack, Tooltip, Typography,
} from '@mui/material';
import {
  KeyboardArrowDownRounded as ChevronIcon,
  CheckCircleRounded as CheckIcon,
  BusinessOutlined as BusinessIcon,
  SettingsOutlined as SettingsIcon,
  LogoutRounded as LogoutIcon,
  MenuRounded as MenuIcon,
  ChevronLeftRounded as BackIcon,
  ChevronRightRounded as ForwardIcon,
} from '@mui/icons-material';
import { useAuthStore } from '../../store/authStore';
import AuthService from '../../api/services/auth.service';
import { landingAfterSwitch } from './landing';
import { sectionForPath } from './navConfig';
import Logo from '../ui/Logo';
import { useBreadcrumbTrail } from './Breadcrumb';
import { useHistoryNav } from './useHistoryNav';
import { layout } from '../../theme';

// The topbar exists to hold the two things that were hardest to find.
//
// 1. WHICH COMPANY YOU ARE OPERATING ON. In a multi-tenant admin this is
//    the single most consequential piece of context on screen, and it used
//    to be visible only if you navigated to Settings. You could delete a
//    user from the wrong company without ever seeing its name.
// 2. WHERE YOU ARE. There were no breadcrumbs anywhere, and detail screens
//    (a document, a workflow run) had no consistent way back.
//
// Logout also lived in two places with two different treatments — the
// sidebar footer, where it disappeared when the sidebar collapsed, and a
// red contained button in the Settings header. It is in the user menu now,
// once.

const avatarColor = (seed: string): string => {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  return `hsl(${Math.abs(hash) % 360}, 45%, 42%)`;
};

interface TopbarProps {
  /** Provided only on a phone, where the rail and panel live in a drawer. */
  onOpenNav?: () => void;
}

const Topbar: React.FC<TopbarProps> = ({ onOpenNav }) => {
  const trail = useBreadcrumbTrail();
  const history = useHistoryNav();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, account, accounts, setAuth, clearAuth } = useAuthStore();

  const [companyAnchor, setCompanyAnchor] = useState<null | HTMLElement>(null);
  const [userAnchor, setUserAnchor] = useState<null | HTMLElement>(null);
  const [switching, setSwitching] = useState(false);

  const section = sectionForPath(pathname);
  // A page's trail that starts with its own section's name ("Fleet" under
  // Fleet, "Users" under Users) would print it twice: the section crumb
  // already links there.
  const crumbs = section && trail[0]?.label === section.label ? trail.slice(1) : trail;
  const multiCompany = (accounts?.length ?? 0) > 1;

  const switchAccount = async (accountId: string) => {
    if (accountId === account?._id || switching) return;
    try {
      setSwitching(true);
      const response = await AuthService.switchAccount({ accountId });
      if (user) setAuth(user, response.token, response.account);
      setCompanyAnchor(null);
      // A hard navigation is the honest move: every open screen is holding
      // data scoped to the old company and there is no cache to invalidate.
      //
      // NOT a reload of the current URL, which is what this used to do.
      // Every id here is company-scoped, so switching company while
      // reading a candidate reloaded that candidate's URL under a company
      // it does not belong to — and the screen said "Could not open that
      // candidate", which reads as a bug in the record rather than as the
      // consequence of switching.
      window.location.assign(landingAfterSwitch(pathname));
    } catch {
      setSwitching(false);
    }
  };

  const logout = () => {
    clearAuth();
    navigate('/login');
  };

  const fullName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
  const initials = `${user?.firstName?.[0] || ''}${user?.lastName?.[0] || ''}`.toUpperCase() || '?';

  return (
    <Box
      component="header"
      sx={{
        height: layout.topbarHeight,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        pr: { xs: 2, lg: 3 },
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* --------------------------------------------------------- logo */}
      {/* The bar spans the full window and the nav starts underneath it, so
          the logo has a proper home. It used to sit inside the sidebar: at
          68px in the rail the wordmark was illegible, and moved into the
          232px panel it left an empty dark square above the rail. This zone
          is exactly rail + panel wide, so the bar reads as the same three
          columns as everything below it. */}
      {/* On a phone the two nav panes are behind this. It sits where the
          rail would be, so the nav is still reached from the same corner. */}
      {onOpenNav && (
        <IconButton onClick={onOpenNav} aria-label="Open navigation" sx={{ ml: 1, mr: 0.5 }}>
          <MenuIcon />
        </IconButton>
      )}

      <Box
        component={RouterLink}
        to="/dashboard"
        aria-label="TopoSDS home"
        sx={{
          // The zone is rail + panel wide so the bar reads as the same three
          // columns as everything below it — but only where those columns
          // exist. On a phone it shrinks to the wordmark.
          width: { xs: 'auto', md: layout.railWidth + layout.panelWidth },
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          pl: { xs: 0, md: 3 },
          textDecoration: 'none',
        }}
      >
        <Box sx={{ display: { xs: 'flex', md: 'none' } }}><Logo height={26} decorative /></Box>
        <Box sx={{ display: { xs: 'none', md: 'flex' } }}><Logo height={38} decorative /></Box>
      </Box>

      {/* -------------------------------------------------------- where */}
      {/* Only rendered below a section root. On /files the section name was
          appearing four times at once — the rail label, a panel title, this
          breadcrumb, and the page's own h1 — none of which told you anything
          the others did not. The rail says which section you are in and the
          h1 names the screen; a breadcrumb is worth the row only when there
          is a level to climb back to. */}
      {/* Indented to the page's own left margin so a crumb sits directly
          above the heading it belongs to. */}
      {/* This column is also the spacer that pushes the company switcher and
          the avatar to the right edge. It keeps `flex: 1` at every width —
          hiding the whole Box on a phone removed the spacer, and the two
          controls bunched up against the logo in the middle of the bar.
          Only the breadcrumb itself is hidden. */}
      <Box sx={{ flex: 1, minWidth: 0, pl: { xs: 1.5, md: 3 }, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {/* Back / forward, as in Finder. Hidden on a phone: the bar has no
            room and the OS already has a back gesture. Each is off when
            there is nowhere inside the app to go — see useHistoryNav. */}
        <Stack direction="row" sx={{ display: { xs: 'none', md: 'flex' }, flexShrink: 0, ml: -1 }}>
          <Tooltip title="Back">
            <span>
              <IconButton size="small" onClick={history.back} disabled={!history.canBack} aria-label="Back">
                <BackIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Forward">
            <span>
              <IconButton size="small" onClick={history.forward} disabled={!history.canForward} aria-label="Forward">
                <ForwardIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
        {trail.length > 0 && (
          <Breadcrumbs
            separator="/"
            sx={{
              minWidth: 0,
              '& .MuiBreadcrumbs-separator': { color: 'text.disabled', mx: 0.75 },
              '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap' },
              '& li': { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
            }}
          >
            {section && (
              <Typography
                component={RouterLink}
                to={section.to}
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {section.label}
              </Typography>
            )}
            {crumbs.map((crumb, i, all) => {
              const last = i === all.length - 1;
              return last || !crumb.to ? (
                <Typography key={i} variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
                  {crumb.label}
                </Typography>
              ) : (
                <Typography
                  key={i}
                  component={RouterLink}
                  to={crumb.to}
                  variant="body2"
                  sx={{ color: 'text.secondary', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                >
                  {crumb.label}
                </Typography>
              );
            })}
          </Breadcrumbs>
        )}
      </Box>

      {/* ------------------------------------------------------ company */}
      <Stack
        direction="row"
        alignItems="center"
        gap={0.75}
        onClick={multiCompany ? (e) => setCompanyAnchor(e.currentTarget) : undefined}
        role={multiCompany ? 'button' : undefined}
        tabIndex={multiCompany ? 0 : undefined}
        sx={{
          px: { xs: 1, sm: 1.6 }, py: 0.85, borderRadius: 2, maxWidth: { xs: 150, sm: 300 }, minWidth: 0,
          border: '1px solid', borderColor: 'divider',
          cursor: multiCompany ? 'pointer' : 'default',
          ...(multiCompany && { '&:hover': { bgcolor: 'grey.50', borderColor: 'grey.300' } }),
        }}
      >
        <BusinessIcon sx={{ fontSize: 19, color: 'text.disabled', flexShrink: 0 }} />
        <Typography
          variant="body1"
          sx={{ fontWeight: 600, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {/* An SA account belongs to no company by design — that is the
              platform hat, not a data problem, so it gets a name rather
              than an apology. */}
          {account?.companyName || (account?.role === 'SA' ? 'TopoSDS platform' : 'No company')}
        </Typography>
        {multiCompany && <ChevronIcon sx={{ fontSize: 20, color: 'text.disabled', flexShrink: 0 }} />}
      </Stack>

      <Menu
        anchorEl={companyAnchor}
        open={!!companyAnchor}
        onClose={() => setCompanyAnchor(null)}
        slotProps={{ paper: { sx: { mt: 1, minWidth: 260 } } }}
      >
        <Typography variant="overline" sx={{ px: 2, py: 0.5, color: 'text.disabled', display: 'block' }}>
          Switch company
        </Typography>
        {(accounts ?? []).map((acc) => {
          const isActive = acc._id === account?._id;
          return (
            <MenuItem key={acc._id} onClick={() => switchAccount(acc._id)} disabled={switching} sx={{ py: 1.25 }}>
              <Stack direction="row" alignItems="center" gap={1.5} sx={{ flex: 1, minWidth: 0 }}>
                <BusinessIcon fontSize="small" sx={{ color: isActive ? 'primary.main' : 'text.disabled' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={isActive ? 700 : 500} noWrap>
                    {acc.companyName || (acc.role === 'SA' ? 'TopoSDS platform' : 'Company')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{acc.role}</Typography>
                </Box>
                {isActive && <CheckIcon fontSize="small" color="primary" />}
              </Stack>
            </MenuItem>
          );
        })}
      </Menu>

      {/* --------------------------------------------------------- user */}
      <Tooltip title={fullName || 'Account'}>
        <IconButton onClick={(e) => setUserAnchor(e.currentTarget)} sx={{ p: 0.5 }}>
          {/* The initials are the FALLBACK, not the avatar. MUI renders
              children only when the image is absent or fails to load, so a
              broken photo URL still gives you "BT" rather than a grey
              circle. */}
          <Avatar
            src={user?.profilePhoto || undefined}
            alt={fullName}
            sx={{ width: 38, height: 38, fontSize: 14, fontWeight: 700, bgcolor: avatarColor(fullName || 'x') }}
          >
            {initials}
          </Avatar>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={userAnchor}
        open={!!userAnchor}
        onClose={() => setUserAnchor(null)}
        slotProps={{ paper: { sx: { mt: 1, minWidth: 230 } } }}
      >
        <Box sx={{ px: 2, py: 1.25 }}>
          <Typography variant="body2" fontWeight={700} noWrap>{fullName || 'Signed in'}</Typography>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
            {user?.email}
          </Typography>
          {account?.role && (
            <Typography variant="caption" color="text.disabled">{account.role}</Typography>
          )}
        </Box>
        <Divider />
        <MenuItem onClick={() => { setUserAnchor(null); navigate('/settings'); }}>
          <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
          Settings
        </MenuItem>
        <MenuItem onClick={logout} sx={{ color: 'error.main' }}>
          <ListItemIcon><LogoutIcon fontSize="small" color="error" /></ListItemIcon>
          Sign out
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default Topbar;
